/**
 * features/admissions/ShareApplicationLink.tsx
 * ------------------------------------------------------------------
 * "Share application link" tab in the Admissions module. The public
 * application form is a PUBLIC route (App.tsx Gate → ?form=apply), so
 * the link is safe to distribute widely. This tab gives admissions
 * staff several ways to hand it out:
 *   - the URL with a one-click Copy
 *   - SEND it from the school by email, branded like every other email
 *     the portal sends, with the QR attached inline. Not a mailto: the
 *     office is not the sender, the wording does not vary by whoever is
 *     at the desk that morning, and every send is recorded so "have we
 *     sent it to the Dubes" has an answer.
 *   - WhatsApp, which SENDS directly once the Meta secrets are
 *     provisioned and otherwise opens a wa.me link — the same button
 *     either way, so the screen does not change when they arrive.
 *   - a QR code (display + download PNG)
 *   - a printable "Apply Here" poster (jsPDF) for the front desk /
 *     notice boards, with the school header, a large QR and the URL.
 *
 * QR generation uses the `qrcode` package → data URL, embeddable both
 * in the DOM <img> and a jsPDF addImage.
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import {
  Link2,
  Copy,
  Check,
  MessageCircle,
  Mail,
  QrCode,
  Download,
  Printer,
  Loader2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { SCHOOL } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { SoftCard } from '@/features/finance/ui';
/* A QR renderer wants a literal colour, not a Tailwind class — so it reads
   the one the palette defines rather than repeating it. A hardcoded hex is
   what carried this school's crest colours into a sister deployment. */
import { BRAND } from '@/lib/brand.palette';

const APPLY_URL = `${SCHOOL.appUrl}/?form=apply`;

export default function ShareApplicationLink() {
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [waNumber, setWaNumber] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [posting, setPosting] = useState(false);

  /* Who it is going to, and anything the office wants to add. The note is
     the reason this beats a template: "your daughter's place for Form 1
     closes on Friday" is what actually gets a form filled in. */
  const [parentName, setParentName] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState<'email' | 'whatsapp' | null>(null);
  const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | null>(null);
  const [waMode, setWaMode] = useState<'api' | 'link' | 'unknown'>('unknown');

  /* Ask once whether direct WhatsApp sending is live, so the button can
     say what it will actually do rather than promising either. */
  useEffect(() => {
    httpsCallable<unknown, { configured: boolean }>(functions, 'getWhatsAppStatus')()
      .then((r) => setWaMode(r.data?.configured ? 'api' : 'link'))
      .catch(() => setWaMode('link'));
  }, []);

  useEffect(() => {
    QRCode.toDataURL(APPLY_URL, {
      width: 512,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: BRAND.primary, light: '#ffffff' },
    })
      .then(setQr)
      .catch(() => setQr(null));
  }, []);

  function copyLink() {
    navigator.clipboard?.writeText(APPLY_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  /**
   * Send it from the school, not from whoever is at the desk.
   *
   * The QR travels with the request so the server can attach it inline —
   * it is already rendered here, and generating it a second time server
   * side would risk the email carrying a different code from the poster
   * on the wall.
   */
  async function sendEmail() {
    const to = emailTo.trim();
    if (!to) return;
    setSending('email');
    setOutcome(null);
    try {
      await httpsCallable<
        { toEmail: string; parentName: string; note: string; qrBase64: string | null },
        { ok: boolean }
      >(functions, 'sendApplicationLinkEmail')({
        toEmail: to, parentName: parentName.trim(), note: note.trim(), qrBase64: qr,
      });
      setOutcome({ ok: true, text: `Sent to ${to}.` });
      setEmailTo('');
    } catch (e) {
      setOutcome({ ok: false, text: (e as Error).message });
    }
    setSending(null);
  }

  /**
   * One button, two behaviours, and the label says which.
   *
   * With the Meta secrets provisioned the server sends it directly.
   * Without them the server hands back a wa.me link and it opens — so the
   * office can use this today, and the day the secrets land nothing on
   * this screen changes.
   */
  async function sendWhatsApp() {
    if (!waNumber.trim()) return;
    setSending('whatsapp');
    setOutcome(null);
    try {
      const res = await httpsCallable<
        { toPhone: string; parentName: string; note: string },
        { ok: boolean; mode: 'api' | 'link'; to: string; link?: string }
      >(functions, 'sendApplicationLinkWhatsApp')({
        toPhone: waNumber, parentName: parentName.trim(), note: note.trim(),
      });

      if (res.data.mode === 'link' && res.data.link) {
        window.open(res.data.link, '_blank', 'noopener');
        setOutcome({ ok: true, text: 'WhatsApp opened with the message ready to send.' });
      } else {
        setOutcome({ ok: true, text: `Sent to ${res.data.to}.` });
        setWaNumber('');
      }
    } catch (e) {
      setOutcome({ ok: false, text: (e as Error).message });
    }
    setSending(null);
  }

  function downloadQr() {
    if (!qr) return;
    const a = document.createElement('a');
    a.href = qr;
    a.download = 'lkc-apply-qr.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function printPoster() {
    setPosting(true);
    try {
      const logo = await loadLogoDataUrl();
      // Hi-res QR for print (larger, higher EC).
      const posterQr = await QRCode.toDataURL(APPLY_URL, {
        width: 900,
        margin: 1,
        errorCorrectionLevel: 'H',
        color: { dark: BRAND.primary, light: '#ffffff' },
      }).catch(() => qr);

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const PW = 210;
      const cx = PW / 2;
      const NAVY: [number, number, number] = [27, 58, 107];
      const GOLD: [number, number, number] = [212, 169, 44];
      const MUTED: [number, number, number] = [107, 114, 128];

      if (logo) {
        try {
          pdf.addImage(logo, 'PNG', cx - 15, 20, 30, 30);
        } catch {
          /* ignore */
        }
      }
      pdf.setTextColor(...NAVY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.text(SCHOOL.name, cx, 62, { align: 'center' });
      pdf.setTextColor(...GOLD);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text(SCHOOL.motto, cx, 70, { align: 'center' });

      pdf.setTextColor(...NAVY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(30);
      pdf.text('APPLY ONLINE', cx, 92, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(13);
      pdf.setTextColor(...MUTED);
      pdf.text('Scan the QR code with your phone camera', cx, 101, { align: 'center' });

      if (posterQr) {
        try {
          pdf.addImage(posterQr, 'PNG', cx - 45, 108, 90, 90);
        } catch {
          /* ignore */
        }
      }

      pdf.setTextColor(...NAVY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.text('or visit', cx, 210, { align: 'center' });
      pdf.setFontSize(16);
      pdf.text(APPLY_URL, cx, 219, { align: 'center' });

      pdf.setTextColor(...MUTED);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.text(
        'The form takes a few minutes and works on any phone.',
        cx,
        233,
        { align: 'center' },
      );
      pdf.setFontSize(10);
      pdf.text(
        `${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`,
        cx,
        280,
        { align: 'center' },
      );

      pdf.save('LKC_Apply_Here_Poster.pdf');
    } finally {
      setPosting(false);
    }
  }

  const inputCls =
    'w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:outline-none focus:border-primaryLight';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ---- LEFT: link + share actions ---- */}
      <div className="space-y-6">
        <SoftCard>
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <Link2 size={16} className="text-primaryLight" /> Application link
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Anyone with this link can apply — no login needed. Share it on your website,
            social media, or with parents directly.
          </p>
          <div className="flex items-center gap-2">
            <input readOnly value={APPLY_URL} className={`${inputCls} font-mono`} />
            <button
              onClick={copyLink}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primaryLight text-white hover:bg-primary"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </SoftCard>

        {/* Shared by both channels, because the message is the same message
            whichever way it travels. The note is what makes this beat a
            template — "Form 1 places close on Friday" is what actually gets
            a form filled in. */}
        <SoftCard>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Link2 size={16} className="text-primaryLight" /> Who it is going to
          </h3>
          <label className="block text-xs text-gray-500 mb-1">Parent or guardian name</label>
          <input
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            placeholder="Mrs Dube — or leave blank for “Dear Parent or Guardian”"
            className={`${inputCls} mb-3`}
          />
          <label className="block text-xs text-gray-500 mb-1">
            Anything to add (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Form 1 places for January close on Friday 26 September."
            className={inputCls}
          />
          <p className="text-[11px] text-gray-400 mt-2">
            The rest of the wording is the school&rsquo;s standard message, so it reads the
            same whoever sends it.
          </p>
        </SoftCard>

        {outcome && (
          <SoftCard
            className={`border-l-4 ${outcome.ok ? 'border-emerald-400' : 'border-red-400'}`}
          >
            <p className={`text-sm ${outcome.ok ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
              {outcome.text}
            </p>
          </SoftCard>
        )}

        <SoftCard>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Mail size={16} className="text-primaryLight" /> Email it
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="parent@example.com"
              className={inputCls}
            />
            <button
              onClick={sendEmail}
              disabled={sending !== null || !emailTo.trim()}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primaryLight text-white hover:bg-primary disabled:opacity-50"
            >
              {sending === 'email'
                ? <Loader2 size={14} className="animate-spin" />
                : <Mail size={14} />}
              {sending === 'email' ? 'Sending' : 'Send'}
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Sent by the school, branded like every other email the portal sends, with the QR
            code attached. Not from your own mailbox.
          </p>
        </SoftCard>

        <SoftCard>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
            <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp it
          </h3>
          <div className="flex items-center gap-2">
            <input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="71 234 567"
              className={inputCls}
            />
            <button
              onClick={sendWhatsApp}
              disabled={sending !== null || !waNumber.trim()}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-[#25D366] text-white hover:brightness-95 disabled:opacity-50"
            >
              {sending === 'whatsapp'
                ? <Loader2 size={14} className="animate-spin" />
                : <MessageCircle size={14} />}
              {waMode === 'api' ? 'Send' : 'Open'}
            </button>
          </div>
          {/* Says which of the two it will do, rather than promising either. */}
          <p className="text-[11px] text-gray-400 mt-2">
            {waMode === 'api'
              ? 'Sent directly by the school through WhatsApp.'
              : 'Opens WhatsApp with the message ready to send. Once the WhatsApp API '
                + 'credentials are set on the server, this same button sends it directly '
                + 'and nothing here changes.'}
          </p>
        </SoftCard>
      </div>

      {/* ---- RIGHT: QR + poster ---- */}
      <div>
        <SoftCard>
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <QrCode size={16} className="text-primaryLight" /> QR code
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Parents scan with their phone camera to open the form.
          </p>
          <div className="flex flex-col items-center">
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              {qr ? (
                <img src={qr} alt="Application QR code" width={220} height={220} />
              ) : (
                <div className="w-[220px] h-[220px] flex items-center justify-center text-gray-300">
                  <Loader2 size={28} className="animate-spin" />
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <button
                onClick={downloadQr}
                disabled={!qr}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <Download size={14} /> Download PNG
              </button>
              <button
                onClick={printPoster}
                disabled={posting}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primaryLight disabled:opacity-50"
              >
                {posting ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
                {posting ? 'Preparing…' : 'Print “Apply Here” poster'}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-3 text-center">
              The poster is an A4 sheet with the school header, a large QR and the link —
              print and post it at the front desk or on notice boards.
            </p>
          </div>
        </SoftCard>
      </div>
    </div>
  );
}
